/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {ModelResponse, PDFElementParams, PDFElementType, PdfFont, ResponseData} from '@dvrd/dvr-controls';
import Account from "./accountModel";
import PDFElement from "./pdfElementModel";
import {createPDFTemplate, previewTemplate, updatePDFTemplate,} from "../util/requestSender";
import {deepClone} from "../util/utils";

interface Props extends BaseProps {
    main_font?: PdfFont;
    account_id?: string;
    account?: (Account | any);
    elements?: (PDFElement | any)[];
}

interface Fields extends BaseFields {
    main_font: PdfFont;
    account_id: string;
    account: Account | null;
    elements: PDFElement[];
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    main_font: PdfFont.HELVETICA,
    account_id: '',
    account: null,
    elements: new Array<PDFElement>(),
};

const fieldsDefinition: { [key in keyof Omit<Fields, 'account' | 'elements'>]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    account_id: createFieldDef(ModelFieldType.STRING),
    main_font: createFieldDef(ModelFieldType.STRING),
};

const ignoreProps: Array<keyof Props> = [];

export default class PDFTemplate extends BaseModel<Props, Fields> {
    constructor(props ?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get mainFont(): PdfFont {
        return this.fields.main_font;
    }

    set mainFont(font: PdfFont) {
        this.fields.main_font = font;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get account(): Account | null {
        return this.fields.account;
    }

    set account(account: Account | null) {
        this.fields.account = account;
        if (account) this.accountId = account.id;
    }

    get elements(): PDFElement[] {
        return this.fields.elements;
    }

    set elements(elements: PDFElement[]) {
        this.fields.elements = elements;
    }

    addElement = (element: PDFElement): PDFTemplate => {
        for (const elem of this.elements)
            if (elem.id === element.id) return this.updateElement(element);
        this.elements = this.elements.concat(element);
        return this;
    };

    updateElement = (element: PDFElement): PDFTemplate => this.removeElement(element).addElement(element);

    removeElement = (element: PDFElement): PDFTemplate => {
        this.elements = this.elements.filter((elem: PDFElement) => elem.id !== element.id);
        return this;
    }

    save = (elements: PDFElementParams<any, any>[], callback: ModelResponse<PDFTemplate>) => {
        let image: File | undefined = undefined;
        for (const element of elements) {
            if (element.type === PDFElementType.IMAGE) {
                if (element.options.img && element.options.img instanceof File) {
                    image = element.options.img;
                    element.options.img = null;
                }
            }
        }
        createPDFTemplate({
            data: {
                account_id: this.accountId,
                elements,
                main_font: this.mainFont,
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.template);
                callback(this, data.success, data);
            },
        });
    };

    update = (elements: PDFElementParams<any, any>[], callback: ModelResponse<PDFTemplate>) => {
        let image: File | undefined = undefined;
        for (const element of elements) {
            if (element.type === PDFElementType.IMAGE) {
                if (element.options.img && element.options.img instanceof File) {
                    image = element.options.img;
                    element.options.img = null;
                }
            }
        }
        updatePDFTemplate({
            data: {
                elements,
                account_id: this.accountId,
                template_id: this.id,
                main_font: this.mainFont,
                image,
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.template);
                callback(this, data.success, data);
            },
        });
    };

    preview = (elements: PDFElementParams<any, any>[], callback: (data: ResponseData, response: Response) => void) => {
        let image: File | undefined = undefined;
        for (const element of elements) {
            if (element.type === PDFElementType.IMAGE) {
                if (element.options.img instanceof File) {
                    image = element.options.img;
                    element.options.img = null;
                }
            }
        }
        previewTemplate({
            account_id: this.accountId,
            data: {
                elements,
                account_id: this.accountId,
                main_font: this.mainFont,
                image,
            },
            callback
        });
    };

    clone = (deep: boolean = true): PDFTemplate => {
        let fields = this.fields;
        if (deep) fields = deepClone(fields);
        return new PDFTemplate(fields);
    }

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.account)
                this.account = props.account instanceof Account ? props.account : new Account(props.account);
            if (props.elements) {
                const elements: PDFElement[] = [];
                for (const elem of props.elements) {
                    const element: PDFElement = elem instanceof PDFElement ? elem : new PDFElement(elem);
                    element.template = this;
                    elements.push(element);
                }
                this.elements = elements;
            }
        }
    }
}